import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { Plus } from "@phosphor-icons/react";

import { resetPortalSessionUrl } from "src/store/slices/AuthenticationSlice";
import {
  setViewChats,
  getAllDraftTypes,
  updateSidenavCollapse,
  setMobileSideNav,
} from "src/store/slices/HomeSlice";
import { getAllThreads } from "src/store/slices/threadsSlice";
import {
  createUserAPI,
  getLawyerHub,
  getUserMetadata,
  setRequestLegalReviewAsideDetails,
  setReTriggerMetadata,
  setShowLawyerCard,
} from "src/store/slices/onboardingSlice";
import {
  resetRedirectTo,
  setAskToDraftIntentWSResponse,
  setAwaitingResponse,
  setMessagingDisabled,
  resetThreadData,
  setResetChatbox,
  closeDocumentPanel,
  getAskThreadMessages,
} from "src/store/slices/messageSlice";
import { trackAnalytics } from "src/pages/helpers";
import { resetPaymentStatus } from "src/store/slices/chatSlice";
import useValidatePageAccess from "src/hooks/useValidatePageAccess";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants";
// Sidenav-driven shelf entries (Contact Lawyer + FreeCallScheduleNudge) were
// removed; suppression-gate spec requires that no consultation entry bypass
// the `eligible_offers.consultation_suppression_reason` gate. The lingering
// imports of `openReferralDrawer` and `setShowContactLawyerPopup` are gone
// with them. See openspec/changes/suppress-consultation-cta-on-handoff-reason/.

import { closeEmailThreadDrawer } from "src/store/slices/emailContextSlice";
import SideNavHeader from "./SideNavHeader.js";
import MainNavItems from "./MainNavItems.js";
import BottomNavItems from "./BottomNavItems.js";
import ToastHandler from "../ToastHandler.js";
import ChatHistory from "../chat-history/index.js";
import PhoneCollectionModal from "src/pages/Modals/PhoneCollectionModal/index.js";
// ChargebeeDropInModal removed — payment flow now uses standalone checkout pages
import SmallScreenModalComponent from "src/pages/Modals/SmallScreenModal";
// PaymentStatusModal removed — payment flow now uses standalone payment-status pages
import BlockedThreadWarning from "../../chat-page/components/BlockedThreadWarning.js";
import LegalReviewShelf from "src/pages/PaymentModals/LegalReviewShelf/index.js";
import UnsupportedFileFormat from "src/pages/Modals/UnsupportedFileFormat";
import DocUploadFailedPopup from "src/pages/Modals/DocUploadFailedPopup";
import ProfileMemoryShelf from "src/pages/ProfileMemory/ProfileMemoryShelf.js";
// import MemoryIntroModal from "src/pages/Modals/MemoryIntroModal.js"; // hidden per Sarthak's request 2026-06-04
import { setShowProfileMemoryPanel, fetchMiscMetadata } from "src/store/slices/profileMemorySlice";
import ContactHelpModal from "src/pages/Modals/ContactHelpModal";
import { REQUEST_STATUS } from "src/domain/messages/requestStatus";

const trackSidenavEvent = (buttonText, userMetadata) => {
  trackAnalytics(
    EVENT_NAMES.CLICKED_BUTTON,
    {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      [EVENT_PARAMETERS.BUTTON_TEXT]: buttonText,
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
      [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
      [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.SIDENAV,
    },
    userMetadata,
  );
};

const SideNav = () => {
  useValidatePageAccess();
  const location = useLocation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sidenavCollapsed = useSelector((state) => state.homeslice.sidenavCollapsed);
  const documentPanelOpen = useSelector((state) => state.messageSlice.documentPanelOpen);
  const emailDrawerOpen = useSelector((state) => state.emailContext?.open);
  // When the doc panel or email drawer is open, force the sidebar into collapsed state
  const effectiveCollapsed = sidenavCollapsed || documentPanelOpen || emailDrawerOpen;
  const viewChats = useSelector((state) => state.homeslice.viewChats);
  const redirectTo = useSelector((state) => state.messageSlice.redirectTo);
  const userMetadataFetchStatus = useSelector((state) => state.onboarding.userMetadataFetchStatus);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const checkoutUrl = useSelector((state) => state.chat.checkoutUrl);
  const threads_status = useSelector((state) => state.threads.threads_status);
  const reTriggerMetadata = useSelector((state) => state.onboarding.reTriggerMetadata);
  const threadData = useSelector((state) => state.messageSlice.threadData);
  const markLegalReviewCallScheduledStatus = useSelector(
    (state) => state.onboarding.markLegalReviewCallScheduledStatus,
  );
  const requestLegalReviewAsideDetails = useSelector((state) => state.onboarding.requestLegalReviewAsideDetails);
  const isSharedThreadPage = useSelector((state) => state.homeslice.isSharedThreadPage);
  const portalSessionUrl = useSelector((state) => state.auth.portalSessionUrl);
  const draftTypesStatus = useSelector((state) => state.homeslice.draftTypesStatus);
  const requestLegalReviewStatus = useSelector((state) => state.onboarding.requestLegalReviewStatus);
  const mobileSideNav = useSelector((state) => state.homeslice.mobileSideNav);
  const storeReferralStatus = useSelector((state) => state.onboarding.storeReferralStatus);
  const smallScreenModalOpen = useSelector((state) => state.homeslice.smallScreenModalOpen);
  const showProfileMemoryPanel = useSelector((state) => state.profileMemory.showPanel);

  const isDocumentLibraryPage = location.pathname === "/document-library";

  const changeSidenavCollapse = () => {
    // If user is expanding the sidenav while doc panel or email drawer is open,
    // close those panels so the sidenav can actually expand
    if (effectiveCollapsed) {
      if (documentPanelOpen) dispatch(closeDocumentPanel());
      if (emailDrawerOpen) dispatch(closeEmailThreadDrawer());
      dispatch(updateSidenavCollapse(false));
    } else {
      dispatch(updateSidenavCollapse(true));
    }
  };

  const hideMobileSideNav = () => {
    dispatch(setMobileSideNav(false));
  };

  const handleChatHistoryClicked = () => {
    trackSidenavEvent("Chats", userMetadata);
    dispatch(setViewChats(!viewChats));
    if (window.innerWidth <= 768) {
      dispatch(setMobileSideNav(false));
    }
  };

  const handleDocumentLibraryClick = () => {
    hideMobileSideNav();
    trackSidenavEvent("Documents", userMetadata);
    dispatch(closeDocumentPanel());
    if (isDocumentLibraryPage) {
      navigate(-1);
    } else {
      navigate("/document-library", { state: { from: location.pathname } });
      dispatch(resetThreadData(null));
    }
  };

  const handleLogoClick = () => {
    if (window.innerWidth < 768) {
      dispatch(setMobileSideNav(false));
    }
    dispatch(closeDocumentPanel());
    dispatch(setAskToDraftIntentWSResponse({}));
    dispatch(resetThreadData());
    navigate("/home");
  };

  const clickNewChat = () => {
    trackSidenavEvent("New Chat", userMetadata);
    if (window.innerWidth < 768) {
      dispatch(setMobileSideNav(false));
    }
    dispatch(closeDocumentPanel());
    dispatch(setAskToDraftIntentWSResponse({}));
    dispatch(resetThreadData());
    dispatch(setResetChatbox(true));
    navigate("/home");
  };

  const handleMemoryClick = () => {
    hideMobileSideNav();
    trackSidenavEvent("Memory", userMetadata);
    dispatch(setShowProfileMemoryPanel(true));
  };

  const handleContactHelpClicked = () => {
    trackSidenavEvent("Contact Help", userMetadata);
    window.open("https://learn.inhouse.app/inhouse-live-support", "_blank", "noopener,noreferrer");
    dispatch(setMobileSideNav(false));
  };

  // Effects
  useEffect(() => {
    if (threads_status === "success") {
      dispatch(getLawyerHub());
    }
  }, [threads_status, dispatch]);

  useEffect(() => {
    if (reTriggerMetadata) {
      dispatch(getUserMetadata());
      dispatch(setReTriggerMetadata(false));
    }
  }, [reTriggerMetadata, dispatch]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      dispatch(updateSidenavCollapse(true));
    }
  }, []); // eslint-disable-line

  // Fetch threads only after getUserMetadata succeeds (valid token confirmed by BaseLayout).
  // Guard with threads_status === "idle" so we only fetch once — subsequent getUserMetadata
  // calls (e.g. after upgrades or scheduling) won't re-fetch the full thread list.
  useEffect(() => {
    if (userMetadataFetchStatus === REQUEST_STATUS.SUCCESS && (threads_status === REQUEST_STATUS.IDLE || threads_status === REQUEST_STATUS.FAILED)) {
      dispatch(getAllThreads());
      dispatch(fetchMiscMetadata());
    }
  }, [userMetadataFetchStatus, threads_status, dispatch]);

  // Group 9 + 10: legacy `?counsel_upgrade=true` (opened FinalizationInfoModal)
  // and `?checkout_id=...` (navigated to `/consultation/checkout/:id`) query
  // params are no longer wired — finalization is gone and checkout is modal-
  // driven. The hook is intentionally a no-op kept for future use.

  useEffect(() => {
    if (storeReferralStatus === "success") {
      dispatch(getUserMetadata());
      dispatch(getAskThreadMessages({ threadId: threadData?.id, dispatchSource: "storeReferral-success", force: true }))
    }
  }, [storeReferralStatus, dispatch]);

  useEffect(() => {
    if (markLegalReviewCallScheduledStatus === "success") {
      dispatch(getUserMetadata());
    }
  }, [markLegalReviewCallScheduledStatus]); // eslint-disable-line

  useEffect(() => {
    if (userMetadataFetchStatus === "profile-not-completed") {
      navigate("/onboarding?utm_source=signup");
    } else if (userMetadataFetchStatus === "profile-not-created") {
      dispatch(createUserAPI());
      navigate("/onboarding?utm_source=signup");
    } else if (userMetadataFetchStatus === "success") {
      if (draftTypesStatus === "idle" || draftTypesStatus === "failed") {
        dispatch(getAllDraftTypes());
      }
    }
  }, [userMetadataFetchStatus]); // eslint-disable-line

  useEffect(() => {
    if (redirectTo) {
      const redirectUrl = redirectTo;
      dispatch(resetRedirectTo(null));
      navigate(redirectUrl);
    }
  }, [redirectTo]); // eslint-disable-line

  useEffect(() => {
    if (portalSessionUrl) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isIOS) {
        window.location.href = portalSessionUrl;
      } else {
        const newTab = window.open(portalSessionUrl, "_blank");

        function checkIfTabClosed() {
          if (newTab?.closed) {
            setTimeout(() => {
              dispatch(getUserMetadata());
            }, 2000);
            clearInterval(checkInterval);
          }
        }
        const checkInterval = setInterval(checkIfTabClosed, 1000);
      }

      dispatch(resetPortalSessionUrl());
    }
  }, [portalSessionUrl]); // eslint-disable-line

  useEffect(() => {
    if (userMetadata.show_new_lawyer_assigned_notification) {
      dispatch(setShowLawyerCard(true));
    }
  }, [userMetadata]); // eslint-disable-line

  useEffect(() => {
    dispatch(setRequestLegalReviewAsideDetails({ show: false }));
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (requestLegalReviewStatus === "success") {
      dispatch(getLawyerHub());
    }
  }, [requestLegalReviewStatus]); // eslint-disable-line

  useEffect(() => {
    if (window.innerWidth < 768) {
      dispatch(updateSidenavCollapse(false));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(resetPaymentStatus());
    dispatch(setMessagingDisabled(false));
    dispatch(setAwaitingResponse(false));
  }, [dispatch]);

  return (
    <>
      <div className="z-[100]" style={{ backgroundColor: "#FDFBF8" }}>
        <div
          className={`side-nav ${effectiveCollapsed === true ? "small" : "large !w-[228px]"
            } h-screen-dvh overflow-y-scroll top-0
                flex py-[24px] px-[16px] flex-col justify-between left-0 ${mobileSideNav ? "block" : "hidden"
            } md:flex absolute md:relative`}
        >
          <div className="relative flex flex-col gap-[24px]">
            <SideNavHeader
              sidenavCollapsed={effectiveCollapsed}
              onHideMobileSideNav={hideMobileSideNav}
              onLogoClick={handleLogoClick}
              onChangeSidenavCollapse={changeSidenavCollapse}
            />
            <div className="flex flex-col gap-[12px] pt-[24px]">
              <div
                data-testid="new-chat-btn"
                className={`flex items-center gap-[8px] h-[40px] cursor-pointer transition-colors ${effectiveCollapsed ? "justify-center px-0" : "px-[8px] rounded-[9999px] hover:bg-[#EFE3D366]"
                  }`}
                onClick={clickNewChat}
              >
                <div className="w-[22px] h-[22px] flex-shrink-0 flex items-center justify-center rounded-full bg-[#EFE3D3]">
                  <Plus size={12} weight="bold" className="text-[#0F0F0F]" />
                </div>
                {!effectiveCollapsed && (
                  <p className="font-['DM_Sans'] font-medium text-[14px] leading-[100%] tracking-[0px] text-center text-[#242424]">
                    New Chat
                  </p>
                )}
              </div>
              <MainNavItems
                sidenavCollapsed={effectiveCollapsed}
                isDocumentLibraryPage={isDocumentLibraryPage}
                viewChats={viewChats}
                onDocumentLibraryClick={handleDocumentLibraryClick}
                onChatHistoryClick={handleChatHistoryClicked}
                onMemoryClick={handleMemoryClick}
              />
            </div>
          </div>
          <BottomNavItems
            sidenavCollapsed={effectiveCollapsed}
            onSupportClick={handleContactHelpClicked}
            onChangeSidenavCollapse={changeSidenavCollapse}
            docPanelOpen={documentPanelOpen}
          />
        </div>
      </div>

      {/* =======================  Modals ========================= */}
      <div>
        <ToastHandler />
        <ChatHistory />
        <UnsupportedFileFormat />
        <DocUploadFailedPopup />
        <BlockedThreadWarning />
        {/* {draftDocumentModalOpen && <DraftDocumentModal />} */}
        <PhoneCollectionModal />
        {smallScreenModalOpen && <SmallScreenModalComponent />}
        {requestLegalReviewAsideDetails.show && <LegalReviewShelf />}
        {showProfileMemoryPanel && <ProfileMemoryShelf />}
        {/* <MemoryIntroModal /> */}
        <ContactHelpModal />
      </div>
    </>
  );
};

export default SideNav;
